/**
 * 图片 File / Blob / DataURL 互转工具（统一图片选择器裁切前后使用）。
 */

import { MAX_IMAGE_UPLOAD_BYTES } from '../constants/base.contants';

/**
 * 将图片 File 读成 DataURL（供 Cropper 预览）。
 * @param file - 本地或网络拉取后的图片文件
 * @returns DataURL 字符串
 */
export async function readImageFileAsDataUrl(file: File | Blob): Promise<string> {
    return await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = (event) => {
            resolve(String(event.target?.result ?? ''));
        };
        reader.onerror = (event) => {
            reject(event);
        };
        reader.readAsDataURL(file);
    });
}

/**
 * 将 Blob 包装为 File（保留 MIME；可指定文件名）。
 * @param blob - 二进制图片
 * @param fileName - 文件名（默认按 MIME 生成）
 */
export function blobToImageFile(blob: Blob, fileName?: string): File {
    // MIME，缺省按 jpeg（fetch-from-url 约定返回 JPEG）
    const mime = (blob.type || 'image/jpeg').toLowerCase();
    // 扩展名
    const ext = mime.includes('png')
        ? 'png'
        : mime.includes('webp')
          ? 'webp'
          : mime.includes('gif')
            ? 'gif'
            : 'jpg';
    // 最终文件名
    const name = (fileName && fileName.trim()) || `image-${Date.now()}.${ext}`;
    return new File([blob], name, { type: mime });
}

/**
 * 判断图片文件是否满足最终上传大小限制。
 * @param file - 待校验的图片 Blob 或 File
 * @returns 文件大小不超过 500KB 时返回 true
 */
export function isImageFileWithinLimit(file: Blob): boolean {
    return file.size <= MAX_IMAGE_UPLOAD_BYTES;
}

/**
 * 从 DataURL 头解析 MIME（如 `image/jpeg`）；解析失败返回空串。
 * @param dataUrl - `data:image/...;base64,...`
 */
export function mimeFromDataUrl(dataUrl: string): string {
    const head = String(dataUrl || '').split(';base64,')[0] || '';
    const mime = head.includes(':') ? head.split(':')[1] : '';
    return mime || '';
}

/**
 * 估算 DataURL 体积（KB）。用于裁切后压缩决策。
 * @param dataUrl - base64 DataURL
 * @param mime - 与 DataURL 一致的 MIME
 */
export function estimateDataUrlSizeKb(dataUrl: string, mime: string): number {
    let str = dataUrl.replace(`data:${mime};base64,`, '');
    const equalIndex = str.indexOf('=');
    if (equalIndex > 0) {
        str = str.substring(0, equalIndex);
    }
    const strLength = str.length;
    const fileLength = strLength - (strLength / 8) * 2;
    const size = (fileLength / 1024).toFixed(2);
    const sizeStr = `${size}`;
    const index = sizeStr.indexOf('.');
    const dou = sizeStr.substr(index + 1, 2);
    if (dou === '00') {
        return Number(sizeStr.substring(0, index) + sizeStr.substr(index + 3, 2));
    }
    return Number(size);
}

/**
 * 将 Shell2 i18n 语言码映射为 Pixabay `lang` 参数。
 * @param appLang - 如 `CN` / `EN` / `IT` / `SP`
 * @returns Pixabay 支持的小写语言码
 */
export function mapAppLangToPixabayLang(appLang?: string): string {
    const key = String(appLang || '')
        .trim()
        .toUpperCase();
    const table: Record<string, string> = {
        CN: 'zh',
        ZH: 'zh',
        EN: 'en',
        IT: 'it',
        SP: 'es',
        ES: 'es',
        DE: 'de',
        FR: 'fr',
        PT: 'pt',
        SL: 'en',
    };
    return table[key] || 'en';
}

/** Pixabay 搜图可用语言选项，value 为 Pixabay 接口语言码。 */
export interface PixabayLanguageOption {
    /** Pixabay 接口语言码 */
    value: string;
    /** 下拉框展示的语言名称 */
    label: string;
}

/** Pixabay 搜图默认语言。 */
export const DEFAULT_PIXABAY_LANGUAGE = 'it';

/** Pixabay 支持的完整语言列表，顺序与接口文档保持一致。 */
export const PIXABAY_LANGUAGE_OPTIONS: PixabayLanguageOption[] = [
    { value: 'cs', label: 'Čeština' },
    { value: 'da', label: 'Dansk' },
    { value: 'de', label: 'Deutsch' },
    { value: 'en', label: 'English' },
    { value: 'es', label: 'Español' },
    { value: 'fr', label: 'Français' },
    { value: 'id', label: 'Bahasa Indonesia' },
    { value: 'it', label: 'Italiano' },
    { value: 'hu', label: 'Magyar' },
    { value: 'nl', label: 'Nederlands' },
    { value: 'no', label: 'Norsk' },
    { value: 'pl', label: 'Polski' },
    { value: 'pt', label: 'Português' },
    { value: 'ro', label: 'Română' },
    { value: 'sk', label: 'Slovenčina' },
    { value: 'fi', label: 'Suomi' },
    { value: 'sv', label: 'Svenska' },
    { value: 'tr', label: 'Türkçe' },
    { value: 'vi', label: 'Tiếng Việt' },
    { value: 'th', label: 'ไทย' },
    { value: 'bg', label: 'Български' },
    { value: 'ru', label: 'Русский' },
    { value: 'el', label: 'Ελληνικά' },
    { value: 'ja', label: '日本語' },
    { value: 'ko', label: '한국어' },
    { value: 'zh', label: '中文' },
];

/**
 * 从 Pixabay hit 取用于 `fetch-from-url` 的地址：优先大图，其次中图，再缩略图。
 * @param hit - 搜图命中项
 */
export function resolvePixabayFetchUrl(hit: {
    largeImageURL?: string;
    webformatURL?: string;
    previewURL?: string;
}): string {
    return (
        String(hit?.largeImageURL || '').trim() ||
        String(hit?.webformatURL || '').trim() ||
        String(hit?.previewURL || '').trim()
    );
}
